import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type {
  UploadTask,
  UploadTaskFile,
  UploadTaskStatus,
  UploadTaskDuplicateItem,
} from '../core/types';
import type { UploadFilePatch } from '../core/uploadClient';
import { throttledUploadTaskStorage } from '../core/persistStorage';

/**
 * 上传任务 Store。
 *
 * **会持久化，但只持久化元信息**：任务快照 + 每个文件的 `{名称, 状态, 进度, ossKey, 失败原因}`，
 * `File` 句柄与 `fileKey` 会被 `partialize` 剥掉（无法序列化 / 重建后无意义）。
 *
 * 持久化的用途不是「刷新后续传」（浏览器不持久化 `File`，做不到），而是：
 * ① 浏览器被强关 / 关机后，靠它把「已成功上传的素材」补一次入库；
 * ② 列表页在服务端计数尚未回写时，用本地进度补齐展示。
 *
 * 写入节流见 `core/persistStorage.ts`（默认 1 次/秒）。
 */

const ACTIVE_STATUSES: UploadTaskStatus[] = ['uploading', 'saving'];

/** 持久化 key */
export const UPLOAD_TASK_STORAGE_KEY = 'material-upload-tasks';

/** 计算批次进度汇总 */
const computeProgress = (files: UploadTaskFile[]): UploadTask['progress'] => {
  const total = files.length;
  const success = files.filter((file) => file.status === 'success').length;
  const failed = files.filter(
    (file) => file.status === 'error' || file.status === 'canceled'
  ).length;
  const settled = success + failed;
  return {
    total,
    success,
    failed,
    // 没有明细（只选空文件夹的任务）视为「无事可做、已完成」
    percent: total === 0 ? 100 : Math.round((settled / total) * 100),
  };
};

interface UploadTaskState {
  tasks: UploadTask[];
  /** 新增一个批次，返回任务快照 */
  addTask: (task: UploadTask) => void;
  /** 合并批次上的补丁（status / errorMessage / duplicates 等） */
  patchTask: (taskId: string, patch: Partial<UploadTask>) => void;
  /** 合并批次内某个文件的补丁，并同步汇总进度 */
  patchFile: (taskId: string, uid: string, patch: UploadFilePatch) => void;
  /** 从批次中移除某个文件（用户取消单文件） */
  removeFile: (taskId: string, uid: string) => void;
  /** 追加一批文件到批次（重试后不会新增，仅用于扩展场景） */
  addFiles: (taskId: string, files: UploadTaskFile[]) => void;
  /** 移除整个批次 */
  removeTask: (taskId: string) => void;
  /** 清空所有已结束的批次 */
  clearFinished: () => void;
  /** 登出时整体重置 */
  reset: () => void;
}

export const useUploadTaskStore = create<UploadTaskState>()(
  persist(
    (set) => ({
      tasks: [],

      addTask: (task) => set((state) => ({ tasks: [task, ...state.tasks] })),

      patchTask: (taskId, patch) =>
        set((state) => ({
          tasks: state.tasks.map((task) => (task.taskId === taskId ? { ...task, ...patch } : task)),
        })),

      patchFile: (taskId, uid, patch) =>
        set((state) => ({
          tasks: state.tasks.map((task) => {
            if (task.taskId !== taskId) return task;
            const files = task.files.map((file) =>
              file.uid === uid ? { ...file, ...patch } : file
            );
            return { ...task, files, progress: computeProgress(files) };
          }),
        })),

      removeFile: (taskId, uid) =>
        set((state) => ({
          tasks: state.tasks.map((task) => {
            if (task.taskId !== taskId) return task;
            const files = task.files.filter((file) => file.uid !== uid);
            return { ...task, files, progress: computeProgress(files) };
          }),
        })),

      addFiles: (taskId, files) =>
        set((state) => ({
          tasks: state.tasks.map((task) => {
            if (task.taskId !== taskId) return task;
            const nextFiles = [...task.files, ...files];
            return {
              ...task,
              files: nextFiles,
              fileCount: nextFiles.length,
              progress: computeProgress(nextFiles),
            };
          }),
        })),

      removeTask: (taskId) =>
        set((state) => ({ tasks: state.tasks.filter((task) => task.taskId !== taskId) })),

      clearFinished: () =>
        set((state) => ({
          tasks: state.tasks.filter((task) => ACTIVE_STATUSES.includes(task.status)),
        })),

      reset: () => set({ tasks: [] }),
    }),
    {
      name: UPLOAD_TASK_STORAGE_KEY,
      version: 2,
      /**
       * v1 → v2：快照字段 `category` 改名为 `folderCategory`（与后端契约一致）。
       * 必须提供 migrate —— 否则版本不匹配时 zustand 会**直接丢弃**整个持久化状态，
       * 让刷新前未回写的任务彻底消失、对账再也跑不起来。
       */
      migrate: (persisted, version) => {
        const state = persisted as { tasks?: UploadTask[] };
        if (version < 2) {
          state.tasks = (state.tasks ?? []).map((task) => {
            const snapshot = task.snapshot as UploadTask['snapshot'] & { category?: string };
            return {
              ...task,
              snapshot: {
                ...snapshot,
                folderCategory: snapshot.folderCategory ?? snapshot.category,
              },
            };
          });
        }
        return state;
      },
      storage: createJSONStorage(() => throttledUploadTaskStorage),
      // 只留可序列化的元信息：File 句柄与 TTUploader 的 fileKey 都不持久化
      partialize: (state) => ({
        tasks: state.tasks.map((task) => ({
          ...task,
          files: task.files.map(({ file: _file, fileKey: _fileKey, ...rest }) => rest),
        })),
      }),
    }
  )
);

/** 进行中（uploading / saving）的批次数，供侧边栏角标使用 */
export const selectActiveTaskCount = (state: UploadTaskState) =>
  state.tasks.reduce(
    (count, task) => (ACTIVE_STATUSES.includes(task.status) ? count + 1 : count),
    0
  );

/** 按 taskId 查找（供明细抽屉使用） */
export const selectTaskById = (state: UploadTaskState, taskId: string | null) =>
  taskId ? (state.tasks.find((task) => task.taskId === taskId) ?? null) : null;

/** 合并一次显式的批次进度重算（例如 duplicates 过滤后） */
export const recomputeTaskProgress = (task: UploadTask): UploadTask => ({
  ...task,
  progress: computeProgress(task.files),
});

/** 过滤出「已成功且未被判定为重复」的文件 infos，口径与 MaterialUploader.onChange 一致 */
export const collectSubmittableInfos = (
  files: UploadTaskFile[],
  duplicateUids: Set<string>,
  submittedUids: string[]
): UploadTaskFile[] =>
  files.filter(
    (file) =>
      file.status === 'success' &&
      !!file.info &&
      !duplicateUids.has(file.uid) &&
      !submittedUids.includes(file.uid)
  );

export type { UploadTaskDuplicateItem };
